import React from 'react';
import './PromptAnalysisView.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { format } from 'date-fns';
import type {
  PromptAnalysis,
  PromptAnalysisAnswers,
  PromptAnalysisBrandInfo,
  PromptAnalysisLiveAnswer,
  PromptAnalysisQuestionType,
  PromptAnalysisResult,
  PromptAnalysisText,
} from '@brand24-original/llm-types';
import { Skeleton } from 'primereact/skeleton';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import BrandIcon from '../../../shared/components/brand-icon/BrandIcon';
import ColorTag, { ColorTagColor } from '../../../shared/components/color-tag/ColorTag';
import { TileBox } from '../../projects/preview/components/tile-box/TileBox';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { SOV_PIE_CHART_PALETTE } from '../../../shared/utils/sovPalette';
import { SELECTED_BRAND_COLOR } from '../../../shared/utils/utils';

/** Slant of the question: positive green, negative red, neutral the default blue. */
const QUESTION_TYPE_COLOR: Record<PromptAnalysisQuestionType, ColorTagColor> = {
  positive: 'green',
  negative: 'red',
  neutral: 'blue',
};

const formatNumber = (value: number) => (Math.round(value * 10) / 10).toString();

/** Inline citations `([host](url))` that ChatGPT appends — the sources are listed in their own table. */
const stripCitations = (text: string) => text.replace(/\s*\(\[[^\]]*\]\([^)]*\)\)/g, '');

/** Adding a prompt to the project from the review; `canAdd(prompt)` = not yet monitored, quota left. */
export interface AddPromptAction {
  canAdd: (prompt: string) => boolean;
  isAdding: (prompt: string) => boolean;
  onAdd: (prompt: string) => void;
}

interface PromptAnalysisViewProps {
  /** null while the history is still loading. */
  analysis: PromptAnalysis | null;
  addPrompt: AddPromptAction;
}

/** House rule: the title sits ABOVE the box, never inside it. */
const Section: React.FC<{
  title: string;
  tooltip?: string;
  aside?: React.ReactNode;
  className?: string;
  /** Without the box — for lists whose items carry their own borders. */
  bare?: boolean;
  children: React.ReactNode;
}> = ({ title, tooltip, aside, className, bare, children }) => (
  <section className={`prompt-analysis-view__section${className ? ` ${className}` : ''}`}>
    <div className="page-box__title-action">
      <div className="page-box__title page-box__title--flex">
        {title}
        {tooltip && <CustomTooltip tooltipText={tooltip} addMargin={false} />}
      </div>
      {aside}
    </div>
    {bare ? children : <div className="prompt-analysis-view__card">{children}</div>}
  </section>
);

const AddButton: React.FC<{ prompt: string; addPrompt: AddPromptAction }> = ({ prompt, addPrompt }) => {
  const { t } = useTranslation();
  if (!addPrompt.canAdd(prompt)) return null;
  const isAdding = addPrompt.isAdding(prompt);
  return (
    <Button
      type="button"
      className="btn btn--sm btn--secondary prompt-analysis-view__add"
      disabled={isAdding}
      onClick={() => addPrompt.onAdd(prompt)}
    >
      <span className={`${isAdding ? 'ico--loader' : 'ico--plus'} ico--xs`} />
      {t('addToProject')}
    </Button>
  );
};

const SummaryTiles: React.FC<{ result: PromptAnalysisResult; verdictLabel: string | null }> = ({
  result,
  verdictLabel,
}) => {
  const { t } = useTranslation();
  const { answers, liveAnswer } = result;
  const tile = (label: string, value: string | number) => (
    <TileBox hasData label={label} tooltipText={`${label}Tooltip`} value={value} trendValue={0} />
  );
  return (
    <div className="tiles tiles--page">
      {answers ? (
        <>
          {tile('promptAnalyst.tileOwnAnswers', `${answers.ownBrand.answers} / ${answers.answers}`)}
          {tile(
            'promptAnalyst.tileAvgPosition',
            answers.ownBrand.avgPosition != null ? formatNumber(answers.ownBrand.avgPosition) : '-'
          )}
          {tile('promptAnalyst.tileBrandsPerAnswer', formatNumber(answers.avgBrandsPerAnswer))}
          {tile('promptAnalyst.tileSourcesPerAnswer', formatNumber(answers.avgSourcesPerAnswer))}
        </>
      ) : liveAnswer ? (
        <>
          {tile('promptAnalyst.tileMentioned', t(liveAnswer.ownBrandPosition != null ? 'yes' : 'no'))}
          {tile(
            'promptAnalyst.tilePosition',
            liveAnswer.ownBrandPosition != null ? `#${liveAnswer.ownBrandPosition}` : '-'
          )}
          {tile('promptAnalyst.tileBrands', liveAnswer.brands.length)}
          {tile('promptAnalyst.tileSources', liveAnswer.sources.length)}
        </>
      ) : null}
      {verdictLabel && tile('promptAnalyst.tileVerdict', verdictLabel)}
    </div>
  );
};

interface BrandRow {
  name: string;
  info: PromptAnalysisBrandInfo;
  /** Measured: answers with the brand and its average position. Live: none — `#` already is the position. */
  cells: string[];
}

/**
 * Brands the answers name, in our DataTable. Icon and colour follow the Brands page: dictionary domain
 * (favicon) with the letter badge as fallback, the project's brand in `SELECTED_BRAND_COLOR` and the
 * rest along `SOV_PIE_CHART_PALETTE`; the project's brand row goes green like other "own" markers.
 */
const BrandsTable: React.FC<{
  answers: PromptAnalysisAnswers | null;
  live: PromptAnalysisLiveAnswer | null;
  brands: Record<string, PromptAnalysisBrandInfo>;
}> = ({ answers, live, brands }) => {
  const { t } = useTranslation();
  const infoOf = (name: string): PromptAnalysisBrandInfo =>
    brands[name] ?? { label: name, domain: null, isOwn: false };
  const rows: BrandRow[] = answers
    ? answers.topBrands.map((b) => ({
        name: b.brand,
        info: infoOf(b.brand),
        cells: [`${b.answers} / ${answers.answers}`, formatNumber(b.avgPosition)],
      }))
    : (live?.brands ?? []).map((brand, index) => ({
        name: brand,
        // The review model matched the own brand in the answer; the dictionary may know it too.
        info: { ...infoOf(brand), isOwn: infoOf(brand).isOwn || index + 1 === live?.ownBrandPosition },
        cells: [],
      }));
  const headers = answers ? [t('promptAnalyst.brandAnswers'), t('promptAnalyst.avgPosition')] : [];

  return (
    <DataTable
      value={rows}
      dataKey="name"
      tableClassName="table"
      className="table-wrapper table-wrapper--stack"
      responsiveLayout="stack"
      rowClassName={(row: BrandRow) => (row.info.isOwn ? 'prompt-analysis-view__row--own' : '')}
      emptyMessage={t('promptAnalyst.noBrands')}
    >
      <Column
        header="#"
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell--center"
        body={(_row: BrandRow, { rowIndex }) => rowIndex + 1}
        style={{ width: '40px' }}
      />
      <Column
        header={t('promptAnalyst.brand')}
        headerClassName="table__header"
        body={(row: BrandRow, { rowIndex }) => (
          <span className="prompt-analysis-view__brand">
            <BrandIcon
              name={row.info.label}
              domain={row.info.domain}
              color={
                row.info.isOwn
                  ? SELECTED_BRAND_COLOR
                  : SOV_PIE_CHART_PALETTE[rowIndex % SOV_PIE_CHART_PALETTE.length]
              }
              size="xs"
            />
            <span className="capitalize">{row.info.label}</span>
            {row.info.isOwn && <ColorTag color="green">{t('brandProjectBadge')}</ColorTag>}
          </span>
        )}
      />
      {headers.map((header, index) => (
        <Column
          key={header}
          header={header}
          headerClassName="table__header"
          body={(row: BrandRow) => row.cells[index]}
          style={{ width: '110px' }}
        />
      ))}
    </DataTable>
  );
};

interface SourceRow {
  domain: string;
  /** Measured only: answers citing the domain. */
  answers?: number;
}

const SourcesTable: React.FC<{ sources: SourceRow[]; totalAnswers?: number }> = ({
  sources,
  totalAnswers,
}) => {
  const { t } = useTranslation();
  return (
    <DataTable
      value={sources}
      dataKey="domain"
      tableClassName="table"
      className="table-wrapper table-wrapper--stack"
      responsiveLayout="stack"
      emptyMessage={t('promptAnalyst.noSources')}
    >
      <Column
        header="#"
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell--center"
        body={(_row: SourceRow, { rowIndex }) => rowIndex + 1}
        style={{ width: '40px' }}
      />
      <Column
        header={t('promptAnalyst.domain')}
        headerClassName="table__header"
        body={(row: SourceRow) => (
          <span className="prompt-analysis-view__brand">
            <BrandIcon name={row.domain} domain={row.domain} size="xs" />
            <a href={`https://${row.domain}`} target="_blank" rel="noopener noreferrer">
              {row.domain}
            </a>
            <span className="ico--external-link ico--xs" />
          </span>
        )}
      />
      {totalAnswers != null && (
        <Column
          header={t('promptAnalyst.brandAnswers')}
          headerClassName="table__header"
          body={(row: SourceRow) => `${row.answers} / ${totalAnswers}`}
          style={{ width: '110px' }}
        />
      )}
    </DataTable>
  );
};

/** What the model searched for — same table as the cited sources; each query opens in Google. */
const SearchQueriesTable: React.FC<{ queries: string[] }> = ({ queries }) => {
  const { t } = useTranslation();
  return (
    <DataTable
      value={queries.map((query) => ({ query }))}
      dataKey="query"
      tableClassName="table"
      className="table-wrapper table-wrapper--stack"
      responsiveLayout="stack"
    >
      <Column
        header="#"
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell--center"
        body={(_row: { query: string }, { rowIndex }) => rowIndex + 1}
        style={{ width: '40px' }}
      />
      <Column
        header={t('promptAnalyst.searchQuery')}
        headerClassName="table__header"
        body={(row: { query: string }) => (
          <span className="prompt-analysis-view__brand">
            <span className="ico--search ico--xs" />
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(row.query)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.query}
            </a>
            <span className="ico--external-link ico--xs" />
          </span>
        )}
      />
    </DataTable>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="prompt-analysis-view__card">
    <Skeleton className="skeleton--table prompt-analysis-view__skeleton">
      <span />
      <span />
      <span />
      <span />
    </Skeleton>
  </div>
);

/**
 * Loading the history or waiting for the worker: the page keeps its layout — skeleton tiles and boxes
 * in place, the status inline in the summary box. Never a full-page loader.
 */
const PendingView: React.FC<{ analysis: PromptAnalysis | null }> = ({ analysis }) => {
  const { t } = useTranslation();
  return (
    <div className="prompt-analysis-view">
      {analysis && (
        <Section title={t('promptAnalyst.summaryTitle')}>
          <p className="prompt-analysis-view__prompt">{analysis.prompt}</p>
          <p
            className={`prompt-analysis-view__status${
              analysis.status === 'FAILED' ? ' prompt-analysis-view__status--failed' : ''
            }`}
          >
            {analysis.status !== 'FAILED' && <span className="ico--loader ico--xs" />}
            {t(analysis.status === 'FAILED' ? 'promptAnalyst.failed' : 'promptAnalyst.inProgress')}
          </p>
        </Section>
      )}
      {analysis?.status !== 'FAILED' && (
        <>
          <div className="tiles tiles--page">
            {[1, 2, 3, 4, 5].map((key) => (
              <TileBox key={key} hasData={false} label="" tooltipText="" value={null} trendValue={0} />
            ))}
          </div>
          <div className="prompt-analysis-view__columns">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </>
      )}
    </div>
  );
};

const PromptAnalysisView: React.FC<PromptAnalysisViewProps> = ({ analysis, addPrompt }) => {
  const { t, i18n } = useTranslation();
  // The review is stored in both languages — switching the panel language needs no new review.
  const lang: keyof PromptAnalysisText = i18n.language.startsWith('pl') ? 'pl' : 'en';

  if (!analysis || analysis.status !== 'DONE' || !analysis.result) {
    return <PendingView analysis={analysis} />;
  }

  const result = analysis.result;
  const { review, answers, liveAnswer, google, verdict } = result;
  // A sample answer only tells whether the prompt is worth adding — "keep" reads wrong there.
  const verdictKey = verdict && !answers && verdict === 'keep' ? 'worth_adding' : verdict;
  const searchQueries = answers?.searchQueries ?? liveAnswer?.searchQueries ?? [];
  const googleQuestions = google ? [...google.peopleAlsoAsk, ...google.relatedSearches] : [];

  return (
    <div className="prompt-analysis-view">
      <Section
        title={t('promptAnalyst.summaryTitle')}
        tooltip="promptAnalyst.summaryTooltip"
        aside={
          <span className="prompt-analysis-view__date">
            {format(new Date(analysis.requestedAt), 'yyyy-MM-dd HH:mm')}
          </span>
        }
      >
        <div className="prompt-analysis-view__prompt-row">
          <p className="prompt-analysis-view__prompt">{analysis.prompt}</p>
          {analysis.monitorId === null && <AddButton prompt={analysis.prompt} addPrompt={addPrompt} />}
        </div>
        <div className="prompt-analysis-view__tags">
          <ColorTag color={QUESTION_TYPE_COLOR[result.questionType]}>
            {t(`promptAnalyst.questionType.${result.questionType}`)}
          </ColorTag>
          {google?.aiOverview && <ColorTag color="grey-dark">{t('promptAnalyst.aiOverview')}</ColorTag>}
          {result.similarAccounts != null && (
            <ColorTag color="grey">
              {t('promptAnalyst.similarAccounts', { value: result.similarAccounts })}
            </ColorTag>
          )}
        </div>
        <p className="prompt-analysis-view__text">{review.summary[lang]}</p>
      </Section>

      {(answers || liveAnswer || verdictKey) && (
        <SummaryTiles
          result={result}
          verdictLabel={verdictKey && t(`promptAnalyst.verdict.${verdictKey}`)}
        />
      )}

      {(answers || liveAnswer) && (
        <div className="prompt-analysis-view__columns">
          <Section
            title={t(answers ? 'promptAnalyst.answersTitle' : 'promptAnalyst.liveTitle')}
            tooltip={answers ? 'promptAnalyst.answersTooltip' : 'promptAnalyst.liveTooltip'}
            bare
          >
            <BrandsTable answers={answers} live={liveAnswer} brands={analysis.brands} />
          </Section>
          <Section
            title={t('promptAnalyst.citedSources')}
            tooltip="promptAnalyst.citedSourcesTooltip"
            bare
          >
            <SourcesTable
              sources={
                answers ? answers.topSources : (liveAnswer?.sources ?? []).map((domain) => ({ domain }))
              }
              totalAnswers={answers?.answers}
            />
          </Section>
        </div>
      )}

      {liveAnswer && (
        <Section title={t('promptAnalyst.answerTitle')} tooltip="promptAnalyst.answerTooltip">
          {/* Markdown like the prompt page's chat response; no raw HTML — the text comes from outside. */}
          <ReactMarkdown className="prompt-analysis-view__answer" remarkPlugins={[remarkGfm]}>
            {stripCitations(liveAnswer.text)}
          </ReactMarkdown>
        </Section>
      )}

      {searchQueries.length > 0 && (
        <Section
          title={t('promptAnalyst.searchQueries')}
          tooltip="promptAnalyst.searchQueriesTooltip"
          bare
        >
          <SearchQueriesTable queries={searchQueries} />
        </Section>
      )}

      {(review.findings.length > 0 || review.rewrites.length > 0) && (
        <div className="prompt-analysis-view__columns">
          {review.findings.length > 0 && (
            <Section title={t('promptAnalyst.improveTitle')} tooltip="promptAnalyst.improveTooltip" bare>
              <ol className="prompt-analysis-view__findings">
                {review.findings.map((finding, index) => (
                  <li key={index} className="prompt-analysis-view__finding">
                    <span className="prompt-analysis-view__number">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <div className="prompt-analysis-view__finding-title">
                        {finding.title[lang]}
                        <ColorTag color="blue">{finding.tag[lang]}</ColorTag>
                      </div>
                      <p className="prompt-analysis-view__text">{finding.description[lang]}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Section>
          )}
          {review.rewrites.length > 0 && (
            <Section
              title={t('promptAnalyst.rewrites')}
              tooltip="promptAnalyst.rewritesTooltip"
              aside={
                <ColorTag color="blue">
                  {t('promptAnalyst.suggestionsCount', { value: review.rewrites.length })}
                </ColorTag>
              }
              bare
            >
              <ul className="prompt-analysis-view__rewrites">
                {review.rewrites.map((rewrite) => (
                  <li key={rewrite.prompt} className="prompt-analysis-view__rewrite">
                    <div>
                      <ColorTag color="green">{rewrite.intent[lang]}</ColorTag>
                      <p className="prompt-analysis-view__rewrite-prompt">{rewrite.prompt}</p>
                    </div>
                    <AddButton prompt={rewrite.prompt} addPrompt={addPrompt} />
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}

      {googleQuestions.length > 0 && (
        <Section title={t('promptAnalyst.peopleTitle')} tooltip="promptAnalyst.peopleTooltip">
          <ul className="prompt-analysis-view__bullets">
            {googleQuestions.map((question) => (
              <li key={question}>{question}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

export default PromptAnalysisView;
